import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { Icon } from '@app/components/Atoms/Icon'
import { fetchLayerDetails } from '@app/services/legendService'
import apiClient from '@app/config/axios.config'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

export interface LayerDetailsReport {
  layer_id: string
  title: string
  icon: Icon
  sources?: {
    title: string
    url: string
  }[]
  source?: string
  additional_information?: {
    title: string
    url: string
  }[]
  sections: {
    title: string
    description: string
  }[]
  images?: {
    url: string
    alt: string
  }[]
  confidence?: {
    title: string
    description: string
    colour: string
  }
  coveredArea?: {
    title: string
    description: string
    regions: string[]
  }
  details?: {
    label: string
    value: string
  }[]
  summary?: string
  startsOpen?: boolean
  accordions?: {
    title: string
    description: string
    startsOpen?: boolean
  }[]
  limitations_caveats_md?: string | null
  use_case_suitability?: {
    use_case_slug: string
    use_case_name: string
    suitability_grade: 'A' | 'B' | 'C' | 'D' | 'E'
    suitability_statement_md: string | null
  }[]
}

export interface LayerDetailsProps {
  layer: string
  displayErrorImage?: boolean
  defaultExpanded?: boolean
  displayErrorMessage?: boolean
}

export const fetchAPILayerDetails = async (
  abortController: AbortController,
  layerAssetTag: string,
) => {
  const url = `/layer/details/${layerAssetTag}`

  try {
    const response = await apiClient.get(url, {
      signal: abortController.signal,
    })

    if (response.status !== 200) return null

    return response.data
  } catch {
    return null
  }
}

// export const useLayerListDetailsFetcher = (
//   layers: MapLayer[],
//   stagedLayers?: MapLayer[],
// ): { layerDetailsList: LayerDetailsReport[][] | null; isLoading: boolean } => {
//   const [isLoading, setIsLoading] = useState(false)

//   const { layerDetailsList, setLayerDetailsList, layerDetailsListCache, setLayerDetailsListCache } =
//     useLayerDetails()

//   useEffect(() => {
//     const abortController = new AbortController()

//     const fetchData = async (layersDefined: MapLayer[]) => {
//       setIsLoading(true)

//       const data = await Promise.all(
//         layersDefined.map(async (layer) => {
//           const layer_id =
//             layer.layerType === 'hazard'
//               ? layer.assetTag
//               : layer.layerType === 'asset'
//               ? layer.type
//               : layer.layerType === 'information'
//               ? layer.id
//               : ''

//           // if layer_id in cache, return cache
//           if (layerDetailsListCache[layer_id]) {
//             return layerDetailsListCache[layer_id]
//           }
//           const layerDetails = await fetchAPILayerDetails(abortController, layer_id)
//           return layerDetails
//         }),
//       )
//       // using data, set the cache to all keys not found in the current cache
//       const newCache = data.reduce<{ [key: string]: LayerDetailsReport[][] | null }>(
//         (acc, layerDetails, index) => {
//           const layer = layersDefined[index]
//           const layer_id =
//             layer.layerType === 'hazard'
//               ? layer.assetTag
//               : layer.layerType === 'asset'
//               ? layer.type
//               : layer.layerType === 'information'
//               ? layer.id
//               : ''

//           if (!layerDetailsListCache[layer_id]) {
//             acc[layer_id] = layerDetails
//           }
//           return acc
//         },
//         {},
//       )
//       if (Object.keys(newCache).length > 0)
//         setLayerDetailsListCache((prev) => ({ ...prev, ...newCache }))

//       setLayerDetailsList(data)
//       setIsLoading(false)
//     }

//     if (layers.length > 0 || (stagedLayers && stagedLayers.length > 0)) {
//       if (stagedLayers && stagedLayers.length > 0) {
//         fetchData([...layers, ...stagedLayers])
//       } else {
//         fetchData(layers)
//       }
//     }

//     return () => abortController.abort()
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [layers, stagedLayers])

//   return { isLoading, layerDetailsList }
// }

export const getLayerId = (l: MapLayer): string => {
  if (l.layerType === 'hazard') return l.assetTag
  if (l.layerType === 'asset') return l.type
  if (l.layerType === 'information') return l.id
  return ''
}

export const useLayerListDetailsFetcher = (
  layers: MapLayer[],
  stagedLayers?: MapLayer[],
): { layerDetailsList: (LayerDetailsReport[][] | null)[]; isLoading: boolean } => {
  // Build a stable, deduplicated list of layer IDs
  const ids = useMemo(() => {
    const all = [...(layers ?? []), ...(stagedLayers ?? [])]
    const out: string[] = []
    const seen = new Set<string>()
    for (const l of all) {
      const id = getLayerId(l)
      if (id && !seen.has(id)) {
        seen.add(id)
        out.push(id)
      }
    }
    return out
  }, [layers, stagedLayers])

  const queryResults = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['layerDetails', id],
      queryFn: () => fetchLayerDetails(id),
      enabled: !!id,
    })),
  })

  const layerDetailsList = queryResults.map(
    (q) => (q.data ?? null) as LayerDetailsReport[][] | null,
  )
  const isLoading = queryResults.some((q) => q.isFetching)

  return { layerDetailsList, isLoading }
}
